import { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Divider,
  IconButton,
  Collapse,
  Drawer,
  Link,
  CircularProgress,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import type { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid';
import { TrendingUp, TrendingDown, X, Plus, Trash2, LayoutGrid, List as ListIcon } from 'lucide-react';
import { usePMNData, useCreateNode, useUpdateNode, useSoftDeleteNode, useCreateEdge } from '@lingaai/shared-hooks';
import { useCurrentUser } from '@lingaai/user-management';
import { formatDate, formatLabel } from '@lingaai/shared-utils';
import { SmartValue } from '../common/SmartValue';
import { useQueryClient } from '@tanstack/react-query';
import { ICON_SIZES, ICON_STROKE } from '../../constants/icons';

// PMN node types
type PMNType = 'Property' | 'Metric' | 'Narrative';

// View mode for PMN sections
type ViewMode = 'cards' | 'table';

// Drag state context
interface DragState {
  isDragging: boolean;
  draggedId: string | null;
  draggedType: PMNType | null;
}

interface PropertiesSectionProps {
  nodeId: string;
  nodeType: string;
}

interface SourceInfo {
  source: string;
  sourceUrl?: string;
  createdAt: string;
  createdBy?: string;
}

interface AttributeItem {
  id: string;
  name: string;
  value: string;
  is_key?: boolean;
  sourceInfo?: SourceInfo;
  history?: AttributeItem[];
}

interface MetricItem {
  id: string;
  name: string;
  value: number;
  unit?: string;
  date?: string;
  is_key?: boolean;
  trend?: 'up' | 'down' | 'flat';
  trendValue?: string;
  sourceInfo?: SourceInfo;
  history?: MetricItem[];
}

interface NarrativeItem {
  id: string;
  content: string;
  date?: string;
  sourceInfo?: SourceInfo;
}

// Add placeholder component - shows + Add, becomes trash when dragging
interface AddPlaceholderProps {
  type: PMNType;
  onAdd: () => void;
  dragState: DragState;
  onTrashDrop: (draggedId: string) => void;
  shortcut?: string;
}

function AddPlaceholder({ type, onAdd, dragState, onTrashDrop, shortcut }: AddPlaceholderProps) {
  const [isDragOver, setIsDragOver] = useState(false);
  const isTrashMode = dragState.isDragging;

  return (
    <Box
      onClick={!isTrashMode ? onAdd : undefined}
      onDragOver={(e) => {
        if (isTrashMode) {
          e.preventDefault();
          setIsDragOver(true);
        }
      }}
      onDragLeave={() => setIsDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setIsDragOver(false);
        if (isTrashMode && dragState.draggedId) {
          onTrashDrop(dragState.draggedId);
        }
      }}
      sx={{
        py: 1.5,
        px: 1.25,
        borderRadius: 1,
        border: '2px dashed',
        borderColor: isDragOver ? 'error.main' : 'divider',
        bgcolor: isDragOver ? 'error.light' : 'transparent',
        cursor: isTrashMode ? 'default' : 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0.5,
        minHeight: 48,
        transition: 'all 0.2s',
        color: 'text.disabled',
        '&:hover': !isTrashMode ? { borderColor: 'primary.main', bgcolor: 'action.hover', color: 'primary.main' } : {},
      }}
    >
      {isTrashMode ? (
        <>
          <Trash2 size={16} />
          <Typography variant="caption" color="error">
            Drop to delete
          </Typography>
        </>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Plus size={16} />
            <Typography variant="caption" color="inherit">
              Add {type === 'Property' ? 'Property' : type}
            </Typography>
          </Box>
          {shortcut && (
            <Typography variant="caption" color="inherit" sx={{ fontSize: '0.65rem', opacity: 0.7 }}>
              ({shortcut})
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}

// Inline edit form for Attribute
interface InlineAttributeFormProps {
  initial?: { name: string; value: string };
  onSave: (name: string, value: string) => void;
  onCancel: () => void;
}

function InlineAttributeForm({ initial, onSave, onCancel }: InlineAttributeFormProps) {
  const [name, setName] = useState(initial?.name || '');
  const [value, setValue] = useState(initial?.value || '');
  const canSave = name.trim() && value.trim();

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && canSave) {
      e.preventDefault();
      onSave(name.trim(), value.trim());
    } else if (e.key === 'Escape') {
      onCancel();
    }
  };

  return (
    <Box
      sx={{
        p: 1,
        bgcolor: 'background.paper',
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'primary.main',
      }}
      onKeyDown={handleKeyDown}
    >
      <TextField size="small" placeholder="Property name" value={name} onChange={(e) => setName(e.target.value)} fullWidth sx={{ mb: 1 }} autoFocus />
      <TextField size="small" placeholder="Value" value={value} onChange={(e) => setValue(e.target.value)} fullWidth sx={{ mb: 1 }} />
      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
        <Button size="small" onClick={onCancel}>
          Cancel
        </Button>
        <Button size="small" variant="contained" onClick={() => onSave(name.trim(), value.trim())} disabled={!canSave}>
          Save
        </Button>
      </Box>
    </Box>
  );
}

// Inline edit form for Metric
interface InlineMetricFormProps {
  initial?: { name: string; value: number; unit?: string };
  onSave: (name: string, value: number, unit?: string) => void;
  onCancel: () => void;
}

function InlineMetricForm({ initial, onSave, onCancel }: InlineMetricFormProps) {
  const [name, setName] = useState(initial?.name || '');
  const [value, setValue] = useState(initial?.value?.toString() || '');
  const [unit, setUnit] = useState(initial?.unit || '');
  const canSave = name.trim() && value.trim();

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && canSave) {
      e.preventDefault();
      onSave(name.trim(), parseFloat(value), unit.trim() || undefined);
    } else if (e.key === 'Escape') {
      onCancel();
    }
  };

  return (
    <Box
      sx={{
        p: 1,
        bgcolor: 'background.paper',
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'primary.main',
      }}
      onKeyDown={handleKeyDown}
    >
      <TextField size="small" placeholder="Metric name" value={name} onChange={(e) => setName(e.target.value)} fullWidth sx={{ mb: 1 }} autoFocus />
      <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
        <TextField size="small" placeholder="Value" type="number" value={value} onChange={(e) => setValue(e.target.value)} sx={{ flex: 2 }} />
        <TextField size="small" placeholder="Unit" value={unit} onChange={(e) => setUnit(e.target.value)} sx={{ flex: 1 }} />
      </Box>
      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
        <Button size="small" onClick={onCancel}>
          Cancel
        </Button>
        <Button size="small" variant="contained" onClick={() => onSave(name.trim(), parseFloat(value), unit.trim() || undefined)} disabled={!canSave}>
          Save
        </Button>
      </Box>
    </Box>
  );
}

// Inline edit form for Fact/Insight
interface InlineContentFormProps {
  initial?: { content: string };
  placeholder: string;
  onSave: (content: string) => void;
  onCancel: () => void;
}

function InlineContentForm({ initial, placeholder, onSave, onCancel }: InlineContentFormProps) {
  const [content, setContent] = useState(initial?.content || '');
  const canSave = content.trim();

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && e.shiftKey && canSave) {
      e.preventDefault();
      onSave(content.trim());
    } else if (e.key === 'Escape') {
      onCancel();
    }
  };

  return (
    <Box
      sx={{
        p: 1,
        bgcolor: 'background.paper',
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'primary.main',
      }}
      onKeyDown={handleKeyDown}
    >
      <TextField size="small" placeholder={placeholder} value={content} onChange={(e) => setContent(e.target.value)} fullWidth multiline rows={2} sx={{ mb: 1 }} autoFocus />
      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1, alignSelf: 'center' }}>
          Shift+Enter to save
        </Typography>
        <Button size="small" onClick={onCancel}>
          Cancel
        </Button>
        <Button size="small" variant="contained" onClick={() => onSave(content.trim())} disabled={!canSave}>
          Save
        </Button>
      </Box>
    </Box>
  );
}

// Delete confirmation dialog
interface DeleteConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  itemType: string;
}

function DeleteConfirmDialog({ open, onClose, onConfirm, itemType }: DeleteConfirmDialogProps) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs">
      <DialogTitle>Delete {itemType}?</DialogTitle>
      <DialogContent>
        <Typography>This will remove the {itemType.toLowerCase()} from this record.</Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={onConfirm} color="error" variant="contained">
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
}

interface PMNTableViewProps {
  attributes: AttributeItem[];
  metrics: MetricItem[];
  narratives: NarrativeItem[];
  onDelete: (ids: string[]) => void;
}

function PMNTableView({ attributes, metrics, narratives, onDelete }: PMNTableViewProps) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const allRows = useMemo(() => {
    const rows: Array<{ id: string; type: PMNType; name: string; value: string; source: string; date: string }> = [];
    attributes.forEach((attr) => {
      rows.push({
        id: attr.id,
        type: 'Property',
        name: attr.name,
        value: attr.value,
        source: attr.sourceInfo?.source || 'user',
        date: attr.sourceInfo?.createdAt ? formatDate(attr.sourceInfo.createdAt) : '',
      });
    });
    metrics.forEach((metric) => {
      rows.push({
        id: metric.id,
        type: 'Metric',
        name: metric.name,
        value: metric.unit ? `${metric.value} ${metric.unit}` : String(metric.value),
        source: metric.sourceInfo?.source || 'user',
        date: metric.sourceInfo?.createdAt ? formatDate(metric.sourceInfo.createdAt) : '',
      });
    });
    narratives.forEach((narrative) => {
      rows.push({
        id: narrative.id,
        type: 'Narrative',
        name: 'narrative',
        value: narrative.content.length > 100 ? narrative.content.slice(0, 100) + '...' : narrative.content,
        source: narrative.sourceInfo?.source || 'user',
        date: narrative.sourceInfo?.createdAt ? formatDate(narrative.sourceInfo.createdAt) : '',
      });
    });
    return rows;
  }, [attributes, metrics, narratives]);

  const columns: GridColDef[] = [
    { field: 'type', headerName: 'Type', width: 100 },
    { field: 'name', headerName: 'Name', flex: 1, minWidth: 150 },
    { field: 'value', headerName: 'Value', flex: 2, minWidth: 200 },
    { field: 'source', headerName: 'Source', width: 120 },
    { field: 'date', headerName: 'Date', width: 150 },
  ];

  const handleDeleteSelected = () => {
    if (selectedIds.size > 0) {
      onDelete(Array.from(selectedIds));
      setSelectedIds(new Set());
    }
  };

  const handleSelectionChange = (newSelection: GridRowSelectionModel) => {
    const ids = 'ids' in newSelection ? Array.from(newSelection.ids) : (newSelection as unknown as string[]);
    setSelectedIds(new Set(ids.map(String)));
  };

  return (
    <Box>
      {selectedIds.size > 0 && (
        <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {selectedIds.size} selected
          </Typography>
          <Button size="small" color="error" variant="outlined" startIcon={<Trash2 size={ICON_SIZES.sm} strokeWidth={ICON_STROKE} />} onClick={handleDeleteSelected}>
            Delete Selected
          </Button>
        </Box>
      )}
      <DataGrid
        rows={allRows}
        columns={columns}
        checkboxSelection
        disableRowSelectionOnClick
        onRowSelectionModelChange={handleSelectionChange}
        pageSizeOptions={[10, 25, 50]}
        initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
        sx={{ minHeight: 400, border: 'none', '& .MuiDataGrid-cell': { borderBottom: '1px solid', borderColor: 'divider' } }}
        density="compact"
      />
    </Box>
  );
}

function formatMetricValue(value: number, unit?: string): string {
  if (unit === 'USD') {
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
    return `$${value.toLocaleString()}`;
  }
  if (unit === '%') return `${value}%`;
  if (unit === 'months') return `${value} mo`;
  return value.toLocaleString();
}

function calculateMetricTrend(current: number, previous: number, unit?: string): { absolute: string; percentage: string; direction: 'up' | 'down' } {
  const absoluteChange = current - previous;
  const percentChange = previous !== 0 ? (absoluteChange / previous) * 100 : 0;

  let absoluteFormatted: string;
  if (unit === 'usd') {
    const absValue = Math.abs(absoluteChange);
    if (absValue >= 1000000) {
      absoluteFormatted = `${(absValue / 1000000).toFixed(1)}M`;
    } else if (absValue >= 1000) {
      absoluteFormatted = `${(absValue / 1000).toFixed(0)}K`;
    } else {
      absoluteFormatted = absValue.toLocaleString();
    }
  } else {
    absoluteFormatted = Math.abs(absoluteChange).toLocaleString();
  }

  const percentFormatted = `${Math.abs(percentChange).toFixed(1)}%`;
  const direction = absoluteChange >= 0 ? 'up' : 'down';

  return {
    absolute: absoluteFormatted,
    percentage: percentFormatted,
    direction,
  };
}

const formatFullDate = formatDate;

/**
 * Unified AMFI Item Block - shows Value, Date, Source, Added By
 * Used for both current value and history items
 */
function AMFIItemBlock({ value, date, sourceInfo, isCurrent = false }: { value: React.ReactNode; date?: string; sourceInfo?: SourceInfo; isCurrent?: boolean }) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 1,
        bgcolor: isCurrent ? 'primary.main' : 'action.hover',
        color: isCurrent ? 'primary.contrastText' : 'text.primary',
      }}
    >
      <Typography variant={isCurrent ? 'h5' : 'body1'} fontWeight={600} sx={{ mb: 0.5, whiteSpace: 'pre-wrap' }}>
        {value}
      </Typography>
      {date && (
        <Typography variant="caption" sx={{ opacity: isCurrent ? 0.8 : 0.7, display: 'block' }}>
          {formatFullDate(date)}
          {isCurrent && ' (Current)'}
        </Typography>
      )}
      {sourceInfo && (
        <Box
          sx={{
            mt: 1,
            pt: 1,
            borderTop: '1px solid',
            borderColor: isCurrent ? 'rgba(255,255,255,0.2)' : 'divider',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption" sx={{ opacity: 0.7 }}>
              Source
            </Typography>
            {sourceInfo.sourceUrl ? (
              <Link href={sourceInfo.sourceUrl} target="_blank" rel="noopener" variant="caption" sx={{ color: isCurrent ? 'inherit' : 'primary.main' }}>
                {sourceInfo.source}
              </Link>
            ) : (
              <Typography variant="caption">{sourceInfo.source}</Typography>
            )}
          </Box>
          {sourceInfo.createdBy && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="caption" sx={{ opacity: 0.7 }}>
                Added By
              </Typography>
              <Typography variant="caption">{sourceInfo.createdBy}</Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}

/**
 * Metric Drawer - unified format
 */
function MetricHistoryDrawer({ open, onClose, metric }: { open: boolean; onClose: () => void; metric: MetricItem }) {
  const hasHistory = metric.history && metric.history.length > 0;
  const previousValue = metric.history?.[0];
  const trend = previousValue?.value !== undefined ? calculateMetricTrend(metric.value, previousValue.value, metric.unit) : null;

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: 400, p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6">{formatLabel(metric.name)}</Typography>
          <IconButton onClick={onClose} size="small">
            <X size={ICON_SIZES.md} strokeWidth={ICON_STROKE} />
          </IconButton>
        </Box>
        <Typography variant="caption" fontWeight={600} sx={{ mb: 1, display: 'block' }}>
          Current
        </Typography>
        <AMFIItemBlock value={formatMetricValue(metric.value, metric.unit)} date={metric.date} sourceInfo={metric.sourceInfo} isCurrent />
        {trend && hasHistory && metric.history && metric.history.length > 0 && (
          <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Change from Previous Period
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {trend.direction === 'up' ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
              <Typography variant="body2" fontWeight={600}>
                {trend.direction === 'up' ? '+' : '-'}
                {trend.absolute}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                ({trend.direction === 'up' ? '+' : '-'}
                {trend.percentage})
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              vs {previousValue && formatMetricValue(previousValue.value, previousValue.unit)} on {previousValue?.date ? formatFullDate(previousValue.date) : 'N/A'}
            </Typography>
          </Box>
        )}
        {hasHistory && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="caption" fontWeight={600} sx={{ mb: 1, display: 'block' }}>
              History
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {metric.history?.map((item) => (
                <AMFIItemBlock key={item.id} value={formatMetricValue(item.value, item.unit)} date={item.date} sourceInfo={item.sourceInfo} />
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </Drawer>
  );
}

/**
 * Attribute Drawer - unified format
 */
function AttributeHistoryDrawer({ open, onClose, attribute }: { open: boolean; onClose: () => void; attribute: AttributeItem }) {
  const hasHistory = attribute.history && attribute.history.length > 0;
  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: 400, p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6">{attribute.name}</Typography>
          <IconButton onClick={onClose} size="small">
            <X size={ICON_SIZES.md} strokeWidth={ICON_STROKE} />
          </IconButton>
        </Box>
        <Typography variant="caption" fontWeight={600} sx={{ mb: 1, display: 'block' }}>
          Current
        </Typography>
        <AMFIItemBlock value={attribute.value} date={attribute.sourceInfo?.createdAt} sourceInfo={attribute.sourceInfo} isCurrent />
        {hasHistory && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="caption" fontWeight={600} sx={{ mb: 1, display: 'block' }}>
              History
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {attribute.history?.map((item) => (
                <AMFIItemBlock key={item.id} value={item.value} date={item.sourceInfo?.createdAt} sourceInfo={item.sourceInfo} />
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </Drawer>
  );
}

/**
 * Polaroid stack effect styles for items with history
 * Creates clean, evenly offset layers like fanned cards
 */
const polaroidStackSx = {
  position: 'relative',
  zIndex: 1,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 3,
    left: 0,
    right: 0,
    bottom: -3,
    bgcolor: 'background.paper',
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 1,
    zIndex: -1,
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 6,
    left: 0,
    right: 0,
    bottom: -6,
    bgcolor: 'background.paper',
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 1,
    zIndex: -2,
  },
};

function Section({ title, children, defaultExpanded = true }: { title: string; children: React.ReactNode; defaultExpanded?: boolean }) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  return (
    <Box sx={{ mb: 2 }}>
      <Typography
        variant="subtitle2"
        fontWeight={600}
        onClick={() => setExpanded(!expanded)}
        sx={{
          cursor: 'pointer',
          py: 1,
          '&:hover': { color: 'primary.main' },
        }}
      >
        {title}
      </Typography>
      <Collapse in={expanded}>
        <Box>{children}</Box>
      </Collapse>
    </Box>
  );
}

function CustomPropertiesGrid({
  attributes,
  nodeId,
  dragState,
  setDragState,
  onDelete,
  isAdding,
  setIsAdding,
}: {
  attributes: AttributeItem[];
  nodeId: string;
  dragState: DragState;
  setDragState: (state: DragState) => void;
  onDelete: (id: string, type: PMNType) => void;
  isAdding: boolean;
  setIsAdding: (v: boolean) => void;
}) {
  const [selectedAttr, setSelectedAttr] = useState<AttributeItem | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const { data: user } = useCurrentUser();
  const createNode = useCreateNode();
  const createEdge = useCreateEdge();
  const updateNode = useUpdateNode();
  const queryClient = useQueryClient();

  const handleAdd = async (name: string, value: string) => {
    if (!user?.tenant_id) return;
    const newNode = await createNode.mutateAsync({
      type: 'Property',
      props: { name, value, source: 'user' },
      tenant_id: user.tenant_id,
    });
    // Create edge to parent
    await createEdge.mutateAsync({
      type: 'HAS_PROPERTY',
      src: nodeId,
      dst: newNode.id,
      props: {},
      tenant_id: user.tenant_id,
    });
    setIsAdding(false);
    queryClient.invalidateQueries({ queryKey: ['pmn-data', nodeId] });
  };

  const handleEdit = async (id: string, name: string, value: string) => {
    await updateNode.mutateAsync({
      id,
      updates: { props: { name, value } },
      nodeType: 'Property',
    });
    setEditingId(null);
    queryClient.invalidateQueries({ queryKey: ['pmn-data', nodeId] });
  };

  return (
    <>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
          gap: 1.5,
        }}
      >
        {attributes.map((attr) => {
          const hasHistory = attr.history && attr.history.length > 0;
          if (editingId === attr.id) {
            return (
              <InlineAttributeForm key={attr.id} initial={{ name: attr.name, value: attr.value }} onSave={(name, value) => handleEdit(attr.id, name, value)} onCancel={() => setEditingId(null)} />
            );
          }
          return (
            <Box
              key={attr.id}
              draggable
              onDragStart={() => setDragState({ isDragging: true, draggedId: attr.id, draggedType: 'Property' })}
              onDragEnd={() => setDragState({ isDragging: false, draggedId: null, draggedType: null })}
              onClick={() => setSelectedAttr(attr)}
              onDoubleClick={(e) => {
                e.stopPropagation();
                setEditingId(attr.id);
              }}
              sx={{
                py: 1,
                px: 1.25,
                minHeight: 60,
                bgcolor: 'action.hover',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
                cursor: 'grab',
                position: 'relative',
                overflow: 'hidden',
                '&:hover': { bgcolor: 'action.selected' },
                '&:hover .delete-icon, &:hover .copy-icon': { opacity: 1 },
                '&:active': { cursor: 'grabbing' },
                ...(hasHistory ? polaroidStackSx : {}),
              }}
            >
              <IconButton
                className="delete-icon"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(attr.id, 'Property');
                }}
                sx={{
                  position: 'absolute',
                  top: 4,
                  right: 4,
                  opacity: 0,
                  transition: 'opacity 0.2s',
                  p: 0.5,
                  '&:hover': { color: 'error.main' },
                }}
              >
                <Trash2 size={ICON_SIZES.sm} strokeWidth={ICON_STROKE} />
              </IconButton>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
                {formatLabel(attr.name)}
              </Typography>
              <SmartValue value={attr.value} name={attr.name} maxLength={40} />
            </Box>
          );
        })}
        {isAdding ? (
          <InlineAttributeForm onSave={handleAdd} onCancel={() => setIsAdding(false)} />
        ) : (
          <AddPlaceholder type="Property" onAdd={() => setIsAdding(true)} dragState={dragState} onTrashDrop={(id) => onDelete(id, 'Property')} shortcut="⌃⌥P" />
        )}
      </Box>
      {selectedAttr && <AttributeHistoryDrawer open={Boolean(selectedAttr)} onClose={() => setSelectedAttr(null)} attribute={selectedAttr} />}
    </>
  );
}

function MetricsList({
  metrics,
  nodeId,
  dragState,
  setDragState,
  onDelete,
  isAdding,
  setIsAdding,
}: {
  metrics: MetricItem[];
  nodeId: string;
  dragState: DragState;
  setDragState: (state: DragState) => void;
  onDelete: (id: string, type: PMNType) => void;
  isAdding: boolean;
  setIsAdding: (v: boolean) => void;
}) {
  const [selectedMetric, setSelectedMetric] = useState<MetricItem | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const { data: user } = useCurrentUser();
  const createNode = useCreateNode();
  const createEdge = useCreateEdge();
  const updateNode = useUpdateNode();
  const queryClient = useQueryClient();

  const handleAdd = async (name: string, value: number, unit?: string) => {
    if (!user?.tenant_id) return;
    const newNode = await createNode.mutateAsync({
      type: 'Metric',
      props: { name, value, unit, source: 'user' },
      tenant_id: user.tenant_id,
    });
    await createEdge.mutateAsync({
      type: 'HAS_METRIC',
      src: nodeId,
      dst: newNode.id,
      props: {},
      tenant_id: user.tenant_id,
    });
    setIsAdding(false);
    queryClient.invalidateQueries({ queryKey: ['pmn-data', nodeId] });
  };

  const handleEdit = async (id: string, name: string, value: number, unit?: string) => {
    await updateNode.mutateAsync({
      id,
      updates: { props: { name, value, unit } },
      nodeType: 'Metric',
    });
    setEditingId(null);
    queryClient.invalidateQueries({ queryKey: ['pmn-data', nodeId] });
  };

  return (
    <>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
          gap: 1.5,
        }}
      >
        {metrics.map((metric) => {
          const hasHistory = metric.history && metric.history.length > 0;
          const trend = metric.history?.[0]?.value !== undefined ? calculateMetricTrend(metric.value, metric.history[0].value, metric.unit) : null;

          if (editingId === metric.id) {
            return (
              <InlineMetricForm
                key={metric.id}
                initial={{ name: metric.name, value: metric.value, unit: metric.unit }}
                onSave={(name, value, unit) => handleEdit(metric.id, name, value, unit)}
                onCancel={() => setEditingId(null)}
              />
            );
          }
          return (
            <Box
              key={metric.id}
              draggable
              onDragStart={() => setDragState({ isDragging: true, draggedId: metric.id, draggedType: 'Metric' })}
              onDragEnd={() => setDragState({ isDragging: false, draggedId: null, draggedType: null })}
              onClick={() => setSelectedMetric(metric)}
              onDoubleClick={(e) => {
                e.stopPropagation();
                setEditingId(metric.id);
              }}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
                py: 1.5,
                px: 1.5,
                minHeight: 80,
                bgcolor: 'action.hover',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
                cursor: 'grab',
                position: 'relative',
                overflow: 'hidden',
                '&:hover': { bgcolor: 'action.selected' },
                '&:hover .delete-icon': { opacity: 1 },
                '&:active': { cursor: 'grabbing' },
                ...(hasHistory ? polaroidStackSx : {}),
              }}
            >
              <IconButton
                className="delete-icon"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(metric.id, 'Metric');
                }}
                sx={{
                  position: 'absolute',
                  top: 4,
                  right: 4,
                  opacity: 0,
                  transition: 'opacity 0.2s',
                  p: 0.5,
                  '&:hover': { color: 'error.main' },
                }}
              >
                <Trash2 size={ICON_SIZES.sm} strokeWidth={ICON_STROKE} />
              </IconButton>
              <Typography variant="h5" fontWeight={600} sx={{ lineHeight: 1.2 }}>
                {formatMetricValue(metric.value, metric.unit)}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.3 }}>
                {formatLabel(metric.name)}
              </Typography>
              {trend && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    mt: 0.5,
                    color: 'text.secondary',
                  }}
                >
                  {trend.direction === 'up' ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                  <Typography variant="caption" fontWeight={500}>
                    {trend.absolute} ({trend.direction === 'up' ? '+' : '-'}
                    {trend.percentage})
                  </Typography>
                </Box>
              )}
            </Box>
          );
        })}
        {isAdding ? (
          <InlineMetricForm onSave={handleAdd} onCancel={() => setIsAdding(false)} />
        ) : (
          <AddPlaceholder type="Metric" onAdd={() => setIsAdding(true)} dragState={dragState} onTrashDrop={(id) => onDelete(id, 'Metric')} shortcut="⌃⌥M" />
        )}
      </Box>
      {selectedMetric && <MetricHistoryDrawer open={Boolean(selectedMetric)} onClose={() => setSelectedMetric(null)} metric={selectedMetric} />}
    </>
  );
}

/**
 * Narrative Drawer - unified format
 */
function NarrativeDetailDrawer({ open, onClose, narrative }: { open: boolean; onClose: () => void; narrative: NarrativeItem }) {
  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: 400, p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6">Narrative</Typography>
          <IconButton onClick={onClose} size="small">
            <X size={ICON_SIZES.md} strokeWidth={ICON_STROKE} />
          </IconButton>
        </Box>
        <AMFIItemBlock value={narrative.content} date={narrative.date || narrative.sourceInfo?.createdAt} sourceInfo={narrative.sourceInfo} isCurrent />
      </Box>
    </Drawer>
  );
}

function NarrativesList({
  narratives,
  nodeId,
  dragState,
  setDragState,
  onDelete,
  isAdding,
  setIsAdding,
}: {
  narratives: NarrativeItem[];
  nodeId: string;
  dragState: DragState;
  setDragState: (state: DragState) => void;
  onDelete: (id: string, type: PMNType) => void;
  isAdding: boolean;
  setIsAdding: (v: boolean) => void;
}) {
  const [selectedNarrative, setSelectedNarrative] = useState<NarrativeItem | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const { data: user } = useCurrentUser();
  const createNode = useCreateNode();
  const createEdge = useCreateEdge();
  const updateNode = useUpdateNode();
  const queryClient = useQueryClient();

  const handleAdd = async (content: string) => {
    if (!user?.tenant_id) return;
    const newNode = await createNode.mutateAsync({
      type: 'Narrative',
      props: { content, source: 'user' },
      tenant_id: user.tenant_id,
    });
    await createEdge.mutateAsync({
      type: 'HAS_NARRATIVE',
      src: nodeId,
      dst: newNode.id,
      props: {},
      tenant_id: user.tenant_id,
    });
    setIsAdding(false);
    queryClient.invalidateQueries({ queryKey: ['pmn-data', nodeId] });
  };

  const handleEdit = async (id: string, content: string) => {
    await updateNode.mutateAsync({
      id,
      updates: { props: { content } },
      nodeType: 'Narrative',
    });
    setEditingId(null);
    queryClient.invalidateQueries({ queryKey: ['pmn-data', nodeId] });
  };

  return (
    <>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: 1,
        }}
      >
        {narratives.map((narrative) => {
          if (editingId === narrative.id) {
            return (
              <InlineContentForm
                key={narrative.id}
                initial={{ content: narrative.content }}
                placeholder="Enter narrative..."
                onSave={(content) => handleEdit(narrative.id, content)}
                onCancel={() => setEditingId(null)}
              />
            );
          }
          return (
            <Box
              key={narrative.id}
              draggable
              onDragStart={() =>
                setDragState({
                  isDragging: true,
                  draggedId: narrative.id,
                  draggedType: 'Narrative',
                })
              }
              onDragEnd={() => setDragState({ isDragging: false, draggedId: null, draggedType: null })}
              onClick={() => setSelectedNarrative(narrative)}
              onDoubleClick={(e) => {
                e.stopPropagation();
                setEditingId(narrative.id);
              }}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
                py: 0.75,
                px: 1.5,
                bgcolor: 'action.hover',
                borderRadius: 1,
                cursor: 'grab',
                position: 'relative',
                '&:hover': { bgcolor: 'action.selected' },
                '&:hover .delete-icon': { opacity: 1 },
                '&:active': { cursor: 'grabbing' },
              }}
            >
              <IconButton
                className="delete-icon"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(narrative.id, 'Narrative');
                }}
                sx={{
                  position: 'absolute',
                  top: 4,
                  right: 4,
                  opacity: 0,
                  transition: 'opacity 0.2s',
                  p: 0.5,
                  '&:hover': { color: 'error.main' },
                }}
              >
                <Trash2 size={ICON_SIZES.sm} strokeWidth={ICON_STROKE} />
              </IconButton>
              <Typography
                variant="body2"
                sx={{
                  flex: 1,
                  overflow: 'hidden',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                }}
              >
                {narrative.content}
              </Typography>
            </Box>
          );
        })}
        {isAdding ? (
          <InlineContentForm placeholder="Enter narrative..." onSave={handleAdd} onCancel={() => setIsAdding(false)} />
        ) : (
          <AddPlaceholder type="Narrative" onAdd={() => setIsAdding(true)} dragState={dragState} onTrashDrop={(id) => onDelete(id, 'Narrative')} shortcut="⌃⌥N" />
        )}
      </Box>
      {selectedNarrative && <NarrativeDetailDrawer open={Boolean(selectedNarrative)} onClose={() => setSelectedNarrative(null)} narrative={selectedNarrative} />}
    </>
  );
}

/**
 * Properties Section - Shows Properties, Metrics, Narratives
 * Core details are shown in the header, not here.
 * Fetches PMN data via graph edges (HAS_PROPERTY, HAS_METRIC, HAS_NARRATIVE).
 */
export function PropertiesSection({ nodeId, nodeType }: PropertiesSectionProps) {
  void nodeType;

  // View mode toggle
  const [viewMode, setViewMode] = useState<ViewMode>('cards');

  // Drag state for AMFI items
  const [dragState, setDragState] = useState<DragState>({
    isDragging: false,
    draggedId: null,
    draggedType: null,
  });

  // Delete confirmation state
  const [deleteConfirm, setDeleteConfirm] = useState<{ id: string; type: PMNType } | null>(null);

  // Bulk delete state (for table view)
  const [bulkDeleteIds, setBulkDeleteIds] = useState<string[] | null>(null);

  // Add form states (lifted from child components for keyboard shortcuts)
  const [isAddingProperty, setIsAddingProperty] = useState(false);
  const [isAddingMetric, setIsAddingMetric] = useState(false);
  const [isAddingNarrative, setIsAddingNarrative] = useState(false);

  const softDeleteNode = useSoftDeleteNode();
  const queryClient = useQueryClient();

  // Keyboard shortcuts: Ctrl+Option+P/M/N
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.altKey) {
        if (e.code === 'KeyP') {
          e.preventDefault();
          setIsAddingProperty(true);
          setIsAddingMetric(false);
          setIsAddingNarrative(false);
        } else if (e.code === 'KeyM') {
          e.preventDefault();
          setIsAddingProperty(false);
          setIsAddingMetric(true);
          setIsAddingNarrative(false);
        } else if (e.code === 'KeyN') {
          e.preventDefault();
          setIsAddingProperty(false);
          setIsAddingMetric(false);
          setIsAddingNarrative(true);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Handle delete confirmation
  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteConfirm) return;
    await softDeleteNode.mutateAsync(deleteConfirm.id);
    queryClient.invalidateQueries({ queryKey: ['pmn-data', nodeId] });
    setDeleteConfirm(null);
  }, [deleteConfirm, softDeleteNode, queryClient, nodeId]);

  // Fetch AMFI data via optimized single query (replaces 8 queries with 1)
  const { data: pmnData, isLoading } = usePMNData(nodeId);

  // Transform graph nodes to display format with versioning
  // Group by name, sort by created_at descending, newest becomes main item, older ones go to history
  const attributes: AttributeItem[] = useMemo(() => {
    if (!pmnData?.properties) return [];
    const allItems = pmnData.properties.map((item) => ({
      id: item.node.id,
      name: (item.node.props.name as string) || 'Unknown',
      value: String(item.node.props.value ?? ''),
      is_key: item.node.props.is_key as boolean | undefined,
      sourceInfo: {
        source: (item.node.props.source as string) || 'user',
        sourceUrl: item.node.props.source_url as string | undefined,
        createdAt: item.node.created_at,
      },
    }));
    // Group by name
    const grouped = new Map<string, AttributeItem[]>();
    for (const item of allItems) {
      const existing = grouped.get(item.name) || [];
      existing.push(item);
      grouped.set(item.name, existing);
    }
    // For each group, sort by createdAt descending, set newest as main, rest as history
    const result: AttributeItem[] = [];
    for (const [, items] of grouped) {
      items.sort((a, b) => new Date(b.sourceInfo?.createdAt || 0).getTime() - new Date(a.sourceInfo?.createdAt || 0).getTime());
      const [newest, ...older] = items;
      if (newest) {
        result.push({ ...newest, history: older.length > 0 ? older : undefined });
      }
    }
    return result;
  }, [pmnData]);

  const metrics: MetricItem[] = useMemo(() => {
    if (!pmnData?.metrics) return [];
    const allItems = pmnData.metrics.map((item) => ({
      id: item.node.id,
      name: (item.node.props.name as string) || 'Unknown',
      value: Number(item.node.props.value) || 0,
      unit: item.node.props.unit as string | undefined,
      date: item.node.props.date as string | undefined,
      trend: item.node.props.trend as 'up' | 'down' | undefined,
      trendValue: item.node.props.trend_value as string | undefined,
      sourceInfo: {
        source: (item.node.props.source as string) || 'user',
        sourceUrl: item.node.props.source_url as string | undefined,
        createdAt: item.node.created_at,
      },
    }));
    // Group by name
    const grouped = new Map<string, MetricItem[]>();
    for (const item of allItems) {
      const existing = grouped.get(item.name) || [];
      existing.push(item);
      grouped.set(item.name, existing);
    }
    // For each group, sort by date field (or createdAt as fallback), set newest as main, rest as history
    const result: MetricItem[] = [];
    for (const [, items] of grouped) {
      items.sort((a, b) => {
        const dateA = new Date(a.date || a.sourceInfo?.createdAt || 0).getTime();
        const dateB = new Date(b.date || b.sourceInfo?.createdAt || 0).getTime();
        return dateB - dateA;
      });
      const [newest, ...older] = items;
      if (newest) {
        result.push({ ...newest, history: older.length > 0 ? older : undefined });
      }
    }
    return result;
  }, [pmnData]);

  const narratives: NarrativeItem[] = useMemo(() => {
    if (!pmnData?.narrative) return [];
    return pmnData.narrative.map((item) => ({
      id: item.node.id,
      content: (item.node.props.content as string) || '',
      narrative_type: item.node.props.narrative_type as 'ai_generated' | 'user' | 'system' | undefined,
      confidence: item.node.props.confidence as number | undefined,
      sourceInfo: {
        source: (item.node.props.source as string) || (item.node.props.narrative_type === 'ai_generated' ? 'AI' : 'user'),
        createdAt: item.node.created_at,
      },
    }));
  }, [pmnData]);

  if (isLoading) {
    return (
      <Paper sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={24} />
      </Paper>
    );
  }

  const handleViewChange = (_: React.MouseEvent<HTMLElement>, newView: ViewMode | null) => {
    if (newView !== null) {
      setViewMode(newView);
    }
  };

  const handleBulkDelete = async (ids: string[]) => {
    setBulkDeleteIds(ids);
  };

  const handleBulkDeleteConfirm = async () => {
    if (!bulkDeleteIds) return;
    for (const id of bulkDeleteIds) {
      await softDeleteNode.mutateAsync(id);
    }
    queryClient.invalidateQueries({ queryKey: ['pmn-data', nodeId] });
    setBulkDeleteIds(null);
  };

  const viewToggle = (
    <ToggleButtonGroup value={viewMode} exclusive onChange={handleViewChange} size="small" aria-label="view mode">
      <ToggleButton value="cards" aria-label="card view">
        <Tooltip title="Card View">
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <LayoutGrid size={ICON_SIZES.md} strokeWidth={ICON_STROKE} />
          </Box>
        </Tooltip>
      </ToggleButton>
      <ToggleButton value="table" aria-label="table view">
        <Tooltip title="Table View">
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <ListIcon size={ICON_SIZES.md} strokeWidth={ICON_STROKE} />
          </Box>
        </Tooltip>
      </ToggleButton>
    </ToggleButtonGroup>
  );

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>{viewToggle}</Box>

      {viewMode === 'table' ? (
        <PMNTableView attributes={attributes} metrics={metrics} narratives={narratives} onDelete={handleBulkDelete} />
      ) : (
        <>
          <Section title="Properties">
            <CustomPropertiesGrid
              attributes={attributes}
              nodeId={nodeId}
              dragState={dragState}
              setDragState={setDragState}
              onDelete={(id, type) => setDeleteConfirm({ id, type })}
              isAdding={isAddingProperty}
              setIsAdding={setIsAddingProperty}
            />
          </Section>
          <Divider sx={{ my: 2 }} />
          <Section title="Metrics">
            <MetricsList
              metrics={metrics}
              nodeId={nodeId}
              dragState={dragState}
              setDragState={setDragState}
              onDelete={(id, type) => setDeleteConfirm({ id, type })}
              isAdding={isAddingMetric}
              setIsAdding={setIsAddingMetric}
            />
          </Section>
          <Divider sx={{ my: 2 }} />
          <Section title="Narratives">
            <NarrativesList
              narratives={narratives}
              nodeId={nodeId}
              dragState={dragState}
              setDragState={setDragState}
              onDelete={(id, type) => setDeleteConfirm({ id, type })}
              isAdding={isAddingNarrative}
              setIsAdding={setIsAddingNarrative}
            />
          </Section>
        </>
      )}

      <DeleteConfirmDialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} onConfirm={handleDeleteConfirm} itemType={deleteConfirm?.type || 'Item'} />
      <DeleteConfirmDialog open={!!bulkDeleteIds} onClose={() => setBulkDeleteIds(null)} onConfirm={handleBulkDeleteConfirm} itemType={`${bulkDeleteIds?.length || 0} items`} />
    </Paper>
  );
}
